import React from 'react';
import { Button, FloatingLabel, Form, Spinner } from 'react-bootstrap';
import Cropper from 'react-easy-crop';
import * as Icon from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';

/**
 * 再利用可能な画像クロッパーコンポーネント
 */
const ImageCropper = ({
  isOpen,
  onClose,
  image,
  crop,
  zoom,
  aspectRatio = 1,
  isProcessing = false,
  onCropChange,
  onZoomChange,
  onCropComplete,
  onSave,
  title = "画像を編集",
  okText = "OK",
  cancelText = "キャンセル",
  className = "",
  style = {},
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleSave = () => {
    if (onSave && !isProcessing) {
      onSave();
    }
  };

  const handleCancel = () => {
    if (onClose && !isProcessing) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancel();
    } else if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    }
  };

  return (
    <div
      className={`image-cropper-overlay ${className}`}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        backgroundColor: "rgba(0, 0, 0, 0.9)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        ...style,
      }}
      onKeyDown={handleKeyDown}
      tabIndex={-1}
    >
      <div style={{ width: "95%", height: "95%", position: "relative" }}>
        {/* ヘッダー */}
        <div
          style={{
            position: "absolute",
            top: "10px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 2001,
            color: "white",
            fontSize: "18px",
            fontWeight: "600",
            textShadow: "0 2px 4px rgba(0,0,0,0.5)",
          }}
        >
          {title}
        </div>

        {/* 閉じるボタン */}
        <Button
          variant="outline-light"
          size="sm"
          style={{
            position: "absolute",
            top: "10px",
            right: "10px",
            zIndex: 2001,
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={handleCancel}
          disabled={isProcessing}
          aria-label="閉じる"
        >
          <Icon.X size={20} />
        </Button>

        {/* Cropperコンポーネント */}
        {image && (
          <Cropper
            image={image}
            crop={crop}
            zoom={zoom}
            aspect={aspectRatio}
            onCropChange={onCropChange}
            onCropComplete={onCropComplete}
            onZoomChange={onZoomChange}
            classes={{
              containerClassName: "cropper-container",
              cropAreaClassName: "crop-area",
            }}
            style={{
              containerStyle: {
                backgroundColor: "transparent",
              },
            }}
          />
        )}

        {/* コントロールパネル */}
        <div
          style={{
            position: "absolute",
            bottom: "20px",
            left: "50%",
            transform: "translateX(-50%)",
            width: "min(400px, 90%)",
            padding: "20px",
            borderRadius: "12px",
            backgroundColor: "#FFFFFF",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            zIndex: 2001,
          }}
        >
          {/* ズームスライダー */}
          <div className="mb-3">
            <label
              htmlFor="zoom-slider"
              className="form-label d-flex align-items-center"
              style={{ fontSize: "14px", color: "#495057", marginBottom: "8px" }}
            >
              <Icon.ZoomIn className="me-2" size={16} />
              ズーム: {Math.round(zoom * 100)}%
            </label>
            <Form.Range
              id="zoom-slider"
              value={zoom}
              min={1}
              max={3}
              step={0.1}
              onChange={(e) => onZoomChange && onZoomChange(parseFloat(e.target.value))}
              disabled={isProcessing}
              style={{
                background: `linear-gradient(to right, #007bff 0%, #007bff ${((zoom - 1) / 2) * 100}%, #dee2e6 ${((zoom - 1) / 2) * 100}%, #dee2e6 100%)`,
              }}
            />
          </div>

          {/* アクションボタン */}
          <div className="d-flex justify-content-between gap-2">
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isProcessing}
              style={{
                borderRadius: "8px",
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: "500",
                minWidth: "100px",
              }}
            >
              <Icon.X className="me-2" size={16} />
              {cancelText}
            </Button>

            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isProcessing || !image}
              style={{
                borderRadius: "8px",
                padding: "10px 20px",
                fontSize: "14px",
                fontWeight: "500",
                minWidth: "100px",
              }}
            >
              {isProcessing ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  処理中...
                </>
              ) : (
                <>
                  <Icon.Check className="me-2" size={16} />
                  {okText}
                </>
              )}
            </Button>
          </div>

          {/* キーボードショートカットのヒント */}
          <div
            className="mt-2 text-center"
            style={{
              fontSize: "12px",
              color: "#6c757d",
            }}
          >
            <Icon.InfoCircle className="me-1" size={12} />
            ESC: キャンセル | Ctrl+Enter: 保存
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
